import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/core/api.service';
import { Base64Service } from 'src/app/core/base64.service';
import { LoggerService } from 'src/app/core/logger.service';
import { ErrCode, ProvisionApiOutput } from 'src/app/shared/models';
import { ProvisionService } from 'src/app/account/services/provision.service';
import { environment } from 'src/environments/environment';
import { ChargebeeProvisionData } from 'src/app/shared/models/provision.model';
import { CookieService } from 'ngx-cookie-service';
import { BlendService } from 'src/app/shared/services/blend.service';
import { SyncCookieService } from 'src/app/core/sync-cookie.service';
import { SentryService } from 'src/app/core/sentry.service';

@Component({
  selector: 'app-provision',
  templateUrl: './provision.component.html',
  styleUrls: ['./provision.component.scss']
})
export class ProvisionComponent implements OnInit {

  protected errCode: ErrCode;
  protected provisionApiOutput: ProvisionApiOutput;
  protected chargebeeProvisionData: ChargebeeProvisionData;
  protected thankyoupage: string;
  protected thankyoupageValues: any;
  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private log: LoggerService,
    private base64Service: Base64Service,
    private provisionService: ProvisionService,
    private cookieService: CookieService,
    private blendService: BlendService,
    private syncCookieService: SyncCookieService,
    private sentryService: SentryService,
  ) {}

  ngOnInit(): void {
    this.blendService.trackUnauthPageview();
    this.signup();
  }

  private async signup() {
    try {
        this.chargebeeProvisionData = await this.apiService.execute('getchargebeeprovisiondata', {
          sub_id: this.route.snapshot?.queryParams?.sub_id,
          invoice_id: this.route.snapshot?.queryParams?.invoice_id
        }) as any;
        const password = this.cookieService.get('pwd');
        const businessTeams = this.chargebeeProvisionData.pricing_model === 'per_unit';
        const source = businessTeams ? 'provisionteam' : 'neworder';
        this.provisionApiOutput = await this.provisionService.provision(
            source,
            this.chargebeeProvisionData.email,
            password,
            this.cookieService.get('_sync_refer') || '',
            1, // allow pass reset
            1, // allow sso
            businessTeams,
            this.chargebeeProvisionData.customer_id
        );
        let accountType = this.chargebeeProvisionData.product_price > 0 ? 'paid' : 'free';
        if (this.chargebeeProvisionData.is_trial) {
          accountType = 'trial';
        }
        this.thankyoupageValues = {
          type: 'new',
          accountType: accountType,
          chargebeeCustomerId: this.chargebeeProvisionData.customer_id,
          userId: this.provisionApiOutput?.user_id?.toString?.(),
          email: this.chargebeeProvisionData.email_sha256,
          sku: this.chargebeeProvisionData.item_price_id,
          planName: this.chargebeeProvisionData.plan_name,
          currency: this.chargebeeProvisionData.currency_code,
          frequency: this.chargebeeProvisionData.billing_period_unit,
          qty: this.chargebeeProvisionData.quantity?.toString?.(),
          trial: this.chargebeeProvisionData.is_trial ? 'Yes' : 'No',
          pricing_model: this.chargebeeProvisionData.pricing_model,
          productPrice: this.chargebeeProvisionData.product_price?.toString?.(),
          amount: this.chargebeeProvisionData.sub_total.toString(),
          discountAmount: this.chargebeeProvisionData.discount?.toString?.(),
          taxAmount: this.chargebeeProvisionData.tax?.toString?.(),
          totalAmount: this.chargebeeProvisionData.total?.toString?.(),
          country: this.chargebeeProvisionData?.billing_address?.country,
          province: this.chargebeeProvisionData?.billing_address?.state,
          sub_id: this.route.snapshot?.queryParams?.sub_id,
          adminId: this.provisionApiOutput?.team_id ?? '',
          orderId: this.chargebeeProvisionData?.invoice_id ?? '',
        };
       const Attribution_Mtags = this.syncCookieService.getMarketingRef();
       await this.blendService.trackSignUp(this.thankyoupageValues.userId, {
          "Attribution_Mtags": Attribution_Mtags ?? "",
          "Product_Billing": this.thankyoupageValues.frequency === 'month' ? 'Monthly' : 'Annual',
          "Product_Discount": this.getUSDValue(this.chargebeeProvisionData.discount),
          "Product_OrderID": this.thankyoupageValues.orderId,
          "Product_Price": this.getUSDValue(this.chargebeeProvisionData.total),
          "Product_SKU": this.thankyoupageValues.sku,
          "Product_Type": this.chargebeeProvisionData.is_trial ? "Trial" : "New",
          "Signup_from": "Website",
          "Signup_type": accountType,
          "seats": this.chargebeeProvisionData.quantity,
          "Product_Family": this.chargebeeProvisionData?.product_family ?? "",
        });
         
        await this.blendService.trackPurchaseCompleted(this.thankyoupageValues.userId, {
          "Product_Billing": this.thankyoupageValues.frequency === 'month' ? 'Monthly' : 'Annual',
          "Product_Discount": this.getUSDValue(this.chargebeeProvisionData.discount),
          "Product_OrderID": this.thankyoupageValues.orderId,
          "Product_Price": this.getUSDValue(this.chargebeeProvisionData.total),
          "Product_SKU": this.thankyoupageValues.sku,
          "Product_Type": this.chargebeeProvisionData.is_trial ? "Trial" : "New",
          "Signup_from": "Website",
          "Signup_type": accountType,
          "seats": this.chargebeeProvisionData.quantity,
          "Attribution_Mtags": Attribution_Mtags ?? "",
          "Product_Family": this.chargebeeProvisionData?.product_family ?? "",
          "platform": "website",
          "team_id":  this.provisionApiOutput?.team_id ?? null
        });
        const queryString = new URLSearchParams(this.thankyoupageValues).toString();
        this.thankyoupage = environment.hubspotHost + '/thank-you-for-choosing-sync?'+queryString;
        this.cookieService.delete('pwd');
        // adding timeout before redirecting so mixpanel event can be triggered.
        setTimeout(() => {
          window.location.href = this.thankyoupage + '#sso' + this.provisionApiOutput.provisionData?.sso?.sso_redirect;
        }, 1000);
    } catch (ex) {
        const log = `Error mk provision data
            exception: ${JSON.stringify(ex, null, 4)}`;
        this.log.error(log);
        this.errCode = ErrCode.fromException(ex);
        this.sentryService.logError(ex, 'Provision failed with '+ this.errCode.code);
    }

  }

  private getUSDValue(value) {
    if (value) {
      return +(value/100).toFixed(2);
    }
    return 0;
  }

}
